import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { 
  Bell, Building2, RefreshCw, CheckCircle, Clock,
  MessageSquare, AlertTriangle, FileText, Send, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const TIPO_ICONS = {
  'info': Bell,
  'warning': AlertTriangle,
  'success': CheckCircle,
  'ticket': MessageSquare,
  'alteracao': FileText
};

const TIPO_COLORS = {
  'info': 'bg-blue-100 text-blue-600',
  'warning': 'bg-amber-100 text-amber-600',
  'success': 'bg-green-100 text-green-600',
  'ticket': 'bg-purple-100 text-purple-600',
  'alteracao': 'bg-slate-100 text-slate-600'
};

export default function NotificacoesAdminPage() {
  const [notificacoes, setNotificacoes] = useState({ notificacoes: [], total: 0, nao_lidas: 0 });
  const [alteracoes, setAlteracoes] = useState({ alteracoes: [], total: 0 });
  const [tickets, setTickets] = useState({ tickets: [], total: 0, pendentes: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('notificacoes');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [resposta, setResposta] = useState('');
  const [selectedAlteracao, setSelectedAlteracao] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [notifRes, altRes, ticketRes] = await Promise.all([
        apiClient.get('/admin/notificacoes'),
        apiClient.get('/admin/notificacoes/alteracoes-entidades'),
        apiClient.get('/admin/notificacoes/tickets')
      ]);
      setNotificacoes(notifRes.data);
      setAlteracoes(altRes.data);
      setTickets(ticketRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const marcarLida = async (id) => {
    try {
      await apiClient.put(`/admin/notificacoes/${id}/marcar-lida`);
      loadData();
      toast.success('Notificação marcada como lida');
    } catch (error) {
      toast.error('Erro ao marcar notificação');
    }
  };

  const marcarTodasLidas = async () => {
    try {
      await apiClient.put('/admin/notificacoes/marcar-todas-lidas');
      loadData();
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar notificações');
    }
  };

  const loadTicketDetalhe = async (ticketId) => {
    try {
      const res = await apiClient.get(`/admin/notificacoes/tickets/${ticketId}`);
      setSelectedTicket(res.data);
    } catch (error) {
      toast.error('Erro ao carregar ticket');
    }
  };

  const loadAlteracaoDetalhe = async (alteracaoId) => {
    try {
      const res = await apiClient.get(`/admin/notificacoes/alteracoes-entidades/${alteracaoId}`);
      setSelectedAlteracao(res.data);
    } catch (error) {
      toast.error('Erro ao carregar alteração');
    }
  };

  const responderTicket = async () => {
    if (!resposta.trim() || !selectedTicket) return;
    try {
      await apiClient.post(`/admin/notificacoes/tickets/${selectedTicket.id}/responder`, null, {
        params: { mensagem: resposta }
      });
      toast.success('Resposta enviada');
      setResposta('');
      loadTicketDetalhe(selectedTicket.id);
      loadData();
    } catch (error) {
      toast.error('Erro ao enviar resposta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notificacoes-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Central de Notificações</h1>
          <p className="text-slate-500">
            Gerir notificações, alterações de entidades e tickets de suporte
          </p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={notificacoes.nao_lidas > 0 ? 'border-blue-200 bg-blue-50/50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Notificações</p>
                <p className="text-2xl font-bold text-blue-600">
                  {notificacoes.nao_lidas}
                  <span className="text-sm font-normal text-slate-400 ml-1">/ {notificacoes.total}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Alterações Entidades</p>
                <p className="text-2xl font-bold text-purple-600">{alteracoes.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={tickets.pendentes > 0 ? 'border-amber-200 bg-amber-50/50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Tickets Pendentes</p>
                <p className="text-2xl font-bold text-amber-600">{tickets.pendentes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Tickets</p>
                <p className="text-2xl font-bold text-green-600">{tickets.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="notificacoes" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notificações
                {notificacoes.nao_lidas > 0 && (
                  <Badge className="bg-blue-600">{notificacoes.nao_lidas}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="alteracoes" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Alterações
              </TabsTrigger>
              <TabsTrigger value="tickets" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Tickets
                {tickets.pendentes > 0 && (
                  <Badge className="bg-amber-600">{tickets.pendentes}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Notificações Tab */}
            <TabsContent value="notificacoes" className="mt-6">
              {notificacoes.nao_lidas > 0 && (
                <div className="flex justify-end mb-4">
                  <Button variant="outline" size="sm" onClick={marcarTodasLidas}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Marcar todas como lidas
                  </Button>
                </div>
              )}
              <div className="space-y-2">
                {notificacoes.notificacoes?.map((notif, idx) => {
                  const Icon = TIPO_ICONS[notif.tipo] || Bell;
                  const colorClass = TIPO_COLORS[notif.tipo] || TIPO_COLORS.info;
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex items-start gap-4 p-4 rounded-lg border ${
                        !notif.lida ? 'bg-blue-50/50 border-blue-200' : 'bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">
                            {notif.titulo || notif.mensagem?.substring(0, 50)}
                          </p>
                          {!notif.lida && (
                            <Badge className="bg-blue-600 text-xs">Nova</Badge>
                          )}
                        </div>
                        {notif.mensagem && notif.titulo && (
                          <p className="text-sm text-slate-600 mt-1">{notif.mensagem}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          {notif.created_at ? new Date(notif.created_at).toLocaleString('pt-PT') : '-'}
                        </p>
                      </div>
                      {!notif.lida && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => marcarLida(notif.id)}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
                {notificacoes.notificacoes?.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Bell className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Nenhuma notificação</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Alterações Tab */}
            <TabsContent value="alteracoes" className="mt-6">
              <div className="space-y-2">
                {alteracoes.alteracoes?.map((alt, idx) => (
                  <div 
                    key={idx}
                    className="flex items-start gap-4 p-4 rounded-lg border bg-white hover:bg-slate-50 cursor-pointer"
                    onClick={() => loadAlteracaoDetalhe(alt.id)}
                  >
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">
                          {alt.instituicao_nome || 'Instituição'}
                        </p>
                        {alt.instituicao_codigo && (
                          <Badge variant="outline" className="text-xs">{alt.instituicao_codigo}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        <Badge variant="secondary" className="mr-2">{alt.acao}</Badge>
                        {alt.descricao || 'Alteração de dados'}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        {alt.timestamp ? new Date(alt.timestamp).toLocaleString('pt-PT') : '-'}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {alteracoes.alteracoes?.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Nenhuma alteração registada</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tickets Tab */}
            <TabsContent value="tickets" className="mt-6">
              <div className="space-y-2">
                {tickets.tickets?.map((ticket, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer hover:bg-slate-50 ${
                      ticket.status === 'Pendente' ? 'bg-amber-50/50 border-amber-200' : 'bg-white'
                    }`}
                    onClick={() => loadTicketDetalhe(ticket.id)}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      ticket.status === 'Pendente' ? 'bg-amber-100' : 'bg-green-100'
                    }`}>
                      <MessageSquare className={`w-5 h-5 ${
                        ticket.status === 'Pendente' ? 'text-amber-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">
                          {ticket.assunto || 'Ticket de Suporte'}
                        </p>
                        <Badge className={
                          ticket.status === 'Pendente' ? 'bg-amber-100 text-amber-700' :
                          ticket.status === 'Em Atendimento' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }>
                          {ticket.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        De: {ticket.instituicao_nome || 'Desconhecido'}
                        {ticket.instituicao_codigo && ` (${ticket.instituicao_codigo})`}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        {ticket.created_at ? new Date(ticket.created_at).toLocaleString('pt-PT') : '-'}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {tickets.tickets?.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Nenhum ticket</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTicket?.assunto || 'Detalhe do Ticket'}
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <Building2 className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="font-medium">{selectedTicket.instituicao?.nome}</p>
                  <p className="text-sm text-slate-500">
                    {selectedTicket.instituicao?.email_principal} | {selectedTicket.instituicao?.telefone}
                  </p>
                </div>
                <Badge className="ml-auto">{selectedTicket.status}</Badge>
              </div>
              
              {/* Messages */}
              <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                <div className="p-4 border-b bg-slate-50">
                  <p className="text-sm text-slate-600">{selectedTicket.mensagem}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(selectedTicket.created_at).toLocaleString('pt-PT')}
                  </p>
                </div>
                {selectedTicket.mensagens?.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 border-b ${
                      msg.tipo === 'resposta_admin' ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-sm">
                        {msg.autor_nome || (msg.tipo === 'resposta_admin' ? 'Admin' : 'Instituição')}
                      </span>
                      {msg.tipo === 'resposta_admin' && (
                        <Badge variant="outline" className="text-xs">Admin</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{msg.mensagem}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(msg.created_at).toLocaleString('pt-PT')}
                    </p>
                  </div>
                ))}
              </div>

              {/* Reply */}
              <div className="space-y-2">
                <Textarea
                  placeholder="Escreva a sua resposta..."
                  value={resposta}
                  onChange={(e) => setResposta(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTicket(null)}>
              Fechar
            </Button>
            <Button onClick={responderTicket} disabled={!resposta.trim()}>
              <Send className="w-4 h-4 mr-2" />
              Enviar Resposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alteração Detail Dialog */}
      <Dialog open={!!selectedAlteracao} onOpenChange={() => setSelectedAlteracao(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhe da Alteração</DialogTitle>
          </DialogHeader>
          {selectedAlteracao && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <Building2 className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="font-medium">{selectedAlteracao.instituicao?.nome}</p>
                  <p className="text-sm text-slate-500">{selectedAlteracao.instituicao?.codigo_ea}</p>
                </div>
                <Badge className="ml-auto">{selectedAlteracao.acao}</Badge>
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Dados Alterados</h4>
                {selectedAlteracao.valores_novos && (
                  <pre className="text-sm bg-slate-50 p-3 rounded overflow-auto max-h-[200px]">
                    {JSON.stringify(selectedAlteracao.valores_novos, null, 2)}
                  </pre>
                )}
                {selectedAlteracao.valores_antigos && (
                  <>
                    <h4 className="font-medium mb-3 mt-4">Valores Anteriores</h4>
                    <pre className="text-sm bg-slate-50 p-3 rounded overflow-auto max-h-[200px]">
                      {JSON.stringify(selectedAlteracao.valores_antigos, null, 2)}
                    </pre>
                  </>
                )}
              </div>
              
              <p className="text-sm text-slate-500">
                Data: {selectedAlteracao.timestamp ? new Date(selectedAlteracao.timestamp).toLocaleString('pt-PT') : '-'}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedAlteracao(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
